import { createContext, useState, useEffect, useContext } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { firestore } from './FirebaseConfig';
import { AuthContext } from './AuthContext';

export const ThemeContext = createContext();

const STORAGE_KEY = 'stamp_app_mode';
const isValidUiMode = (value) => value === 'light-mode' || value === 'dark-mode';

const getInitialUiMode = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (isValidUiMode(cached)) return cached;
  } catch {
    // ignore
  }
  return 'dark-mode';
};

/**
 * ThemeProvider component that manages the application's theme
 * Syncs theme with Firebase and provides theme-related functions to all components
 */
export const ThemeProvider = ({ children }) => {
  const [uiMode, setUiMode] = useState(getInitialUiMode);
  const { currentUser } = useContext(AuthContext);

  // Load theme from Firebase on initial render
  useEffect(() => {
    const loadThemeFromFirebase = async () => {
      if (!currentUser?.company?.company) return;

      try {
        const settingsRef = doc(
          firestore,
          'clients',
          currentUser.company.company,
          'client_settings',
          'bale_feed_settings',
        );
        const docSnap = await getDoc(settingsRef);

        if (docSnap.exists()) {
          const appMode = docSnap.data()?.stamp_app_mode;
          if (isValidUiMode(appMode)) setUiMode(appMode);
        }
      } catch (error) {
        console.error('Error loading theme from Firebase:', error);
      }
    };

    loadThemeFromFirebase();
  }, [currentUser]);

  // Apply theme to body element
  useEffect(() => {
    document.body.className = uiMode;
    try {
      localStorage.setItem(STORAGE_KEY, uiMode);
    } catch {
      // ignore
    }
  }, [uiMode]);

  // Function to update UI mode in Firebase
  const updateUiMode = async (newMode, persistToFirestore = true) => {
    if (!isValidUiMode(newMode)) newMode = 'dark-mode';

    setUiMode(newMode);

    if (!persistToFirestore || !currentUser?.company?.company) return;

    const settingsRef = doc(firestore, 'clients', currentUser.company.company, 'client_settings', 'bale_feed_settings');

    try {
      const docSnap = await getDoc(settingsRef);
      if (docSnap.exists()) {
        await updateDoc(settingsRef, { stamp_app_mode: newMode });
      } else {
        await setDoc(settingsRef, { stamp_app_mode: newMode });
      }
    } catch (error) {
      console.error('Error updating UI mode:', error);
    }
  };

  return <ThemeContext.Provider value={{ uiMode, updateUiMode }}>{children}</ThemeContext.Provider>;
};

// Custom hook for easier theme access
export const useTheme = () => useContext(ThemeContext);
